import { DcaStrategy, CoinModelRequest } from "@/types/dca";
import { get } from "lodash";
import { useTranslation } from "react-i18next";

interface RequirementStatus {
  id: string;
  label: string;
  completed: boolean;
  message: string;
  type: 'error' | 'warning' | 'success';
}

export const useDcaRequirements = (dca: Partial<DcaStrategy>) => {
  const { t } = useTranslation();
  const baseMinimumInvestment = 10;
  
  const coins: CoinModelRequest[] = get(dca, "coinModelRequest", []);
  const coinCount = coins.length;
  const investedCapital = get(dca, "investedCapital", 0);
  const minimumInvestmentAmount = get(dca, "minimumInvestmentAmount", 0);
  const frequency = get(dca, "frequency", "");
  const performanceObjective = get(dca, "performanceObjective", 0);
  const alertAndKeepPortfolio = get(dca, "alertAndKeepPortfolio", false);
  const alertAndSellPortfolio = get(dca, "alertAndSellPortfolio", false);
  
  const minimumRequired = coinCount * baseMinimumInvestment;

  const requirements: RequirementStatus[] = [
    {
      id: 'coins',
      label: t('dca.requirements.coins'),
      completed: coinCount > 0,
      message: coinCount > 0 
        ? t('dca.requirements.coins_added', { count: coinCount })
        : t('dca.requirements.coins_missing'),
      type: coinCount > 0 ? 'success' : 'error'
    },
    {
      id: 'balance',
      label: t('dca.requirements.dca_balance'),
      completed: investedCapital >= minimumRequired && coinCount > 0,
      message: investedCapital >= minimumRequired && coinCount > 0
        ? t('dca.requirements.balance_sufficient', { amount: investedCapital })
        : t('dca.requirements.balance_insufficient', { required: minimumRequired }),
      type: investedCapital >= minimumRequired && coinCount > 0 ? 'success' : 'error'
    },
    {
      id: 'investment',
      label: t('dca.requirements.investment_amount'),
      completed: minimumInvestmentAmount >= minimumRequired && minimumInvestmentAmount <= investedCapital && coinCount > 0,
      message: minimumInvestmentAmount >= minimumRequired && minimumInvestmentAmount <= investedCapital && coinCount > 0
        ? t('dca.requirements.investment_valid', { amount: minimumInvestmentAmount })
        : t('dca.requirements.investment_invalid', { min: minimumRequired, max: investedCapital }),
      type: minimumInvestmentAmount >= minimumRequired && minimumInvestmentAmount <= investedCapital && coinCount > 0 ? 'success' : 'error'
    },
    {
      id: 'frequency',
      label: t('dca.requirements.frequency'),
      completed: !!frequency,
      message: frequency 
        ? t('dca.requirements.frequency_selected', { frequency })
        : t('dca.requirements.frequency_missing'),
      type: frequency ? 'success' : 'error'
    },
    {
      id: 'objective',
      label: t('dca.requirements.performance_objective'),
      completed: performanceObjective >= 1 && performanceObjective <= 100,
      message: performanceObjective >= 1 && performanceObjective <= 100
        ? t('dca.requirements.objective_valid', { value: performanceObjective })
        : t('dca.requirements.objective_invalid'),
      type: performanceObjective >= 1 && performanceObjective <= 100 ? 'success' : 'error'
    },
    {
      id: 'alerts',
      label: t('dca.requirements.alert_configuration'),
      completed: alertAndKeepPortfolio || alertAndSellPortfolio,
      message: alertAndKeepPortfolio || alertAndSellPortfolio
        ? t('dca.requirements.alert_configured')
        : t('dca.requirements.alert_missing'),
      type: alertAndKeepPortfolio || alertAndSellPortfolio ? 'success' : 'error'
    }
  ];

  const completedCount = requirements.filter(r => r.completed).length;
  const totalCount = requirements.length;
  const isReadyToActivate = completedCount === totalCount;
  const completionPercentage = Math.round((completedCount / totalCount) * 100);

  return {
    requirements,
    completedCount,
    totalCount,
    isReadyToActivate,
    completionPercentage
  };
};
